import { useEffect, useRef } from "react";
import { History } from "lucide-react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../stores/auth";
import { api } from "../lib/api";
import { fmtDate, timeAgo } from "../lib/format";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";
import {
  notificationTypeLabel,
  resolveNotificationPath,
  type NotificationItem,
} from "../lib/notifications";
import { Button, PageLoader, Spinner } from "../components/ui";
import { PageHeader } from "../components/storefront/PageHeader";

const PAGE_SIZE = 20;

export function Notifications() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { moduleEnabled } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const listQuery = useInfiniteQuery({
    queryKey: ["notifications", "all"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        pageSize: String(PAGE_SIZE),
      });
      return api<{ items: NotificationItem[]; total: number; unread: number }>(`/notifications?${params}`);
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
    staleTime: 15_000,
  });

  const markReadMut = useMutation({
    mutationFn: (ids?: string[]) => api("/notifications/read", { method: "POST", body: ids ? { ids } : {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const items = flattenInfinitePages(listQuery.data?.pages);
  const total = listQuery.data?.pages[0]?.total ?? 0;
  const unread = listQuery.data?.pages[0]?.unread ?? 0;
  const hasNextPage = items.length < total && (listQuery.hasNextPage ?? false);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !listQuery.isFetchingNextPage) {
          void listQuery.fetchNextPage();
        }
      },
      { root, rootMargin: "120px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, listQuery.isFetchingNextPage, listQuery.fetchNextPage, items.length]);

  function openNotification(item: NotificationItem) {
    if (!item.readAt) {
      markReadMut.mutate([item.id]);
    }
    const path = resolveNotificationPath(item, { collectionEnabled: moduleEnabled("collection") });
    if (path) navigate(path);
  }

  if (listQuery.isLoading) return <PageLoader />;

  return (
    <div className="flex max-h-[calc(100dvh-3.5rem-var(--mobile-nav-height))] min-h-0 flex-col px-4 pt-4 pb-4 lg:max-h-[calc(100dvh-4.5rem)] lg:px-6 lg:pb-6">
      <Link to="/account" className="shrink-0 text-xs font-medium text-brand-pink lg:hidden">
        ← Account
      </Link>

      <div className="mt-4 shrink-0 space-y-4">
        <PageHeader
          title="Notifications"
          description={unread > 0 ? `${unread} unread` : "You're all caught up"}
        />

        {unread > 0 && (
          <Button variant="outline" onClick={() => markReadMut.mutate(undefined)}>
            Mark all read
          </Button>
        )}
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-white/5 bg-surface lg:min-h-[420px]">
        {items.length ? (
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`flex w-full gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-white/[0.03] ${
                  item.readAt ? "opacity-75" : "bg-brand-pink/[0.05]"
                }`}
                onClick={() => openNotification(item)}
              >
                <History size={16} className="mt-0.5 shrink-0 text-white/35" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white/90">{item.title}</p>
                  {(item.body || item.type) && (
                    <p className="mt-0.5 text-xs text-white/55">{item.body ?? notificationTypeLabel(item.type)}</p>
                  )}
                  <p className="mt-1 text-[10px] text-white/35" title={fmtDate(item.createdAt)}>
                    {timeAgo(item.createdAt)}
                  </p>
                </div>
              </button>
            ))}

            <div ref={sentinelRef} className="flex min-h-12 items-center justify-center py-3">
              {listQuery.isFetchingNextPage && <Spinner className="h-5 w-5" />}
            </div>
          </div>
        ) : (
          <p className="px-4 py-16 text-center text-sm text-white/40">No notifications yet</p>
        )}
      </div>
    </div>
  );
}
